import React, { useState, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import { createV2Transaction, addStockToSubPortfolioTwo, updateStockInSubPortfolioTwo, searchMasterAssets } from '../composables/fetchAPI';
import { usePortfolio } from '../context/PortfolioContext';

interface AddEditStockProps {
  isOpen: boolean;
  onClose: () => void;
  portfolio: any; // รับ portfolio object
  initialData?: any;
  onRefresh?: () => void;
  onOpenHoldingAllocation?: () => void; // เพิ่ม prop สำหรับเปิด EditHoldingAllocationModal
  portfolioId?: string; // รับ portfolioId มาตรงๆ จาก props
  setIsConfigPopupOpen: (isOpen: boolean) => void;
  isAllocationOpen?: boolean; // เพิ่ม prop สำหรับเช็คว่า EditHoldingAllocationModal เปิดอยู่หรือไม่
  subPortfolio?: any; // เพิ่ม prop สำหรับ subPortfolio
}
// ✅ เพิ่ม Interface สำหรับรับข้อมูลจาก API
interface AssetResult {
  match_score: number | null;
  assetType: string;
  displaySymbol: string;
  displayName: string;
  providerSymbol: string;
  subtitle: string;
}
const AddEditStock: React.FC<AddEditStockProps> = ({
  isOpen,
  onClose,
  portfolio,
  initialData,
  onRefresh,
  portfolioId,
  setIsConfigPopupOpen,
  onOpenHoldingAllocation,
  isAllocationOpen,
  subPortfolio
}) => {
  const [quantity, setQuantity] = useState<number | ''>('');
  const [avgBuyPrice, setAvgBuyPrice] = useState<number | ''>('');
  const [ratio, setRatio] = useState<number | ''>('');
  const [symbol, setSymbol] = useState('');
  const { displayMode, refreshUserPortfolios, userPortfolios } = usePortfolio(); // ✅ ดึง displayMode จาก context


  // ✅ States ใหม่สำหรับระบบค้นหา
  const [results, setResults] = useState<{ symbol: string; name: string; subtitle: string }[]>([]);
  const [suggestedStocks, setSuggestedStocks] = useState<{ symbol: string; name: string; subtitle: string }[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout | null>(null);


  // ✅ ฟังก์ชันดึง Suggestion (หุ้นแนะนำ/พบบ่อย)
  const loadSuggestions = async () => {
    if (suggestedStocks.length > 0) return;
    setSuggestLoading(true);
    try {
      const response = await searchMasterAssets();
      const items: AssetResult[] = response.success && response.items ? response.items : [];
      setSuggestedStocks(
        items.map(item => ({
          symbol: item.displaySymbol,
          name: item.displayName,
          subtitle: item.subtitle,
        }))
      );
    } catch (error) {
      console.error('Error loading suggestions:', error);
    } finally {
      setSuggestLoading(false);
    }
  };

  // ✅ ฟังก์ชันจัดการเมื่อพิมพ์ค้นหา (Debounce 300ms)
  const handleSymbolChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim().toUpperCase();
    setSymbol(value);

    if (typingTimeout) clearTimeout(typingTimeout);

    if (value.length < 1) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const timeout = setTimeout(async () => {
      try {
        const response = await searchMasterAssets(value);
        const items: AssetResult[] = response.success && response.items ? response.items : [];
        setResults(
          items.map(item => ({
            symbol: item.displaySymbol,
            name: item.displayName,
            subtitle: item.subtitle,
          }))
        );
        setShowDropdown(true);
      } catch (error) {
        console.error('Error searching stocks:', error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    setTypingTimeout(timeout);
  };

  // ✅ เมื่อกดเลือกหุ้นจาก Dropdown
  const handleSymbolSelect = (selectedSymbol: string) => {
    setSymbol(selectedSymbol);
    setResults([]);
    setShowDropdown(false);
  };
  const isSuggestMode = symbol.length === 0;
  const displayList = isSuggestMode ? suggestedStocks : results;
  const isLoadingActive = isSuggestMode ? suggestLoading : loading;



  const currentPortfolio = useMemo(() => {
    return userPortfolios.find(p => (p.portfolioId || p.portId || p.id) === portfolioId);
  }, [userPortfolios, portfolioId]);

  const calculatedRemainRatio = useMemo(() => {
    const currentSubPort = portfolio?.subPorts?.find(
      (sp: any) => sp.subPortId === (initialData?.subPortId || subPortfolio?.subPortId)
    ) || portfolio;

    const stocks = currentSubPort.holdings || currentSubPort.stocks || [];

    const totalRatio = stocks.reduce((sum: number, stock: any) => {
      // ถ้าแก้ไขหุ้นเดิม
      if (initialData && stock.stockSymbol === initialData?.stockSymbol) {
        return sum + (Number(ratio) || 0);
      }
      return sum + (stock.expectedRatio || 0);
    }, 0);

    // ✅ ถ้าเป็น "หุ้นใหม่" (initialData เป็น null) ให้เอาค่า ratio ใน Input มาบวกเพิ่มเข้าไปด้วย
    const finalTotalRatio = initialData ? totalRatio : (totalRatio + (Number(ratio) || 0));

    return 100 - finalTotalRatio;
  }, [portfolio, initialData, ratio, subPortfolio]);


  useEffect(() => {
    if (initialData && isOpen) {
      setQuantity(initialData.quantity ?? '');
      setAvgBuyPrice(initialData.avgBuyPrice ?? '');
      setSymbol(initialData.stockSymbol || initialData.symbol || '');
      setRatio(initialData.ratio ?? initialData.expectedRatio ?? '');
    }
    // ✅ ใช้ key ที่เป็นค่าพื้นฐาน (ไม่ใช่ reference ของ object) กัน Effect ยิงซ้ำทุกครั้งที่ parent re-render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialData?.stockSymbol, initialData?.subPortId]);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (quantity === '' || avgBuyPrice === '') {
      toast.error('กรุณากรอกข้อมูลให้ครบถ้วน');
      return;
    }

    const data = {
      symbol,
      quantity: Number(quantity),
      avgBuyPrice: Number(avgBuyPrice),
      ratio: Number(ratio)
    };
    const subPortId = portfolio?.subPortId || subPortfolio?.subPortId;

    try {
      if (displayMode === 'V1') {
        // --- LOGIC V1 ---
        if (initialData) {
          await updateStockInSubPortfolioTwo(portfolioId!, subPortId, symbol, {
            stockSymbol: symbol,
            quantity: data.quantity,
            avgBuyPrice: data.avgBuyPrice,
            expectedRatio: data.ratio,
          });
          toast.success('แก้ไขหุ้นสำเร็จ!');
        } else {
          await addStockToSubPortfolioTwo(portfolioId!, subPortId, {
            stockSymbol: data.symbol,
            quantity: data.quantity,
            avgBuyPrice: data.avgBuyPrice,
            expectedRatio: data.ratio,
          });
          toast.success('เพิ่มหุ้นสำเร็จ!');
        }
      } else {
        // --- LOGIC V2 ---
        await createV2Transaction(portfolioId!, {
          type: 'ADJUST_HOLDING',
          subPortId: initialData.subPortId,
          assetId: initialData.assetId, // ใช้ assetId จาก initialData
          targetQuantity: Number(quantity),
          targetAvgPrice: Number(avgBuyPrice),
          adjustmentReason: 'BROKER_RECONCILE',
          transactionDate: new Date().toISOString(),
          note: 'ปรับจำนวนและต้นทุนให้ตรงกับข้อมูลจาก broker',
          // สร้าง idempotencyKey แบบง่ายๆ โดยใช้ timestamp + symbol
          idempotencyKey: `adjust-${initialData.assetSymbol}-${Date.now()}`,
          source: 'USER',
          // metadata: {
          //   brokerQuantity: Number(quantity),
          //   brokerAvgPrice: Number(avgBuyPrice),
          //   reasonDetail: 'manual reconcile from broker statement'
          // }
        });
        toast.success('ปรับปรุงข้อมูลหุ้นสำเร็จ');
      }

      // --- หลังจากจบโหมดไหนก็ตาม ให้ทำการ Refresh และปิด Modal ---
      if (onRefresh) onRefresh(); // จะเรียก refreshUserPortfolios ที่คุณส่งเข้ามา
      onClose();

    } catch (error: any) {
      console.error(error);
      toast.error('เกิดข้อผิดพลาดในการบันทึกข้อมูล');
    }
  };

  return (
    <div className={`fixed inset-0 z-[90] flex items-center justify-center p-4 `}>
      <div className="absolute inset-0  bg-black/60 backdrop-blur-sm" onClick={onClose}></div>
      <div className={`relative w-full max-w-sm bg-[var(--component-bg-color)] border border-[var(--border-item-light)] rounded-xl shadow-2xl p-6 animate-fadeIn ${isAllocationOpen ? 'md:-translate-x-[52%]' : ''}`}>
        <h2 className="text-xl font-bold mb-6" style={{ color: 'var(--text-color)' }}>
          {initialData ? `แก้ไขหุ้น: ${initialData?.stockSymbol}` : 'เพิ่มหุ้นใหม่'}
        </h2>

        {displayMode === 'V2' && (
          <div className="mb-6 p-3 bg-[var(--yellow-note-bg)] border border-[var(--yellow-note-border)] rounded-lg">
            <p className="text-xs text-[var(--yellow-text-color)] leading-relaxed">
              ⚠️ <strong>คำเตือน:</strong> การแก้ไขข้อมูลแบบ Manual โดยไม่ได้ผ่านรายการ ซื้อ/ขาย จริง อาจทำให้ค่ากำไร/ขาดทุน (Realized Gain) และต้นทุนรวมในพอร์ตของคุณคำนวณคลาดเคลื่อนได้
            </p>
          </div>
        )}
        <div className="space-y-4">
          <div className="relative">
            <label className="block text-sm mb-1" style={{ color: 'var(--text-color)' }}>สัญลักษณ์ (Ticker)<span className='text-[var(--red-text-color)]'>*</span></label>
            <input
              type="text"
              value={symbol}
              onChange={handleSymbolChange}
              onFocus={() => {
                if (!initialData) {
                  if (symbol.length === 0) loadSuggestions();
                  setShowDropdown(true);
                }
              }}
              style={{ backgroundColor: 'var(--text-input)' }}
              onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
              disabled={!!initialData}
              className={`w-full px-3 py-2 rounded border border-[var(--border-item-light)] text-[var(--text-color)] focus:outline-none focus:border-[var(--yellow-text-color)] ${initialData ? 'cursor-not-allowed opacity-60' : ''}`}
              placeholder="เช่น AAPL, NVDA"
            />

            {/* ✅ Dropdown แสดงผลลัพธ์การค้นหา (ใช้สีจาก theme.css) */}
            {showDropdown && !initialData && (
              <ul className='absolute left-0 right-0 bg-[var(--bg-item)] border border-[var(--border-item-light)] rounded-lg shadow-xl mt-1 z-50 max-h-48 overflow-y-auto'>
                {isSuggestMode && !suggestLoading && suggestedStocks.length > 0 && (
                  <li className='px-4 py-2 text-xs text-[var(--yellow-text-color)] bg-[var(--component-bg-color)] font-bold sticky top-0 border-b border-[var(--border-item-light)] z-20'>
                    🔥 หุ้นตัวโปรด
                  </li>
                )}

                {isLoadingActive && (
                  <li className='px-4 py-3 text-sm text-[var(--text-color)] opacity-60 text-center'>กำลังโหลดข้อมูล...</li>
                )}

                {!isLoadingActive && displayList.map((item) => (
                  <li
                    key={item.symbol}
                    className={`px-4 py-3 hover:bg-[var(--hover-item)] cursor-pointer text-[var(--text-color)] flex items-center justify-between border-b border-[var(--border-item-light)] last:border-0`}
                    onMouseDown={() => handleSymbolSelect(item.symbol)}
                  >
                    <div className='flex items-center gap-3 overflow-hidden'>
                      {isSuggestMode && <span title="Suggested" className="text-[var(--yellow-text-color)] text-lg">★</span>}
                      <div className='flex flex-col overflow-hidden'>
                        <div className='flex items-baseline gap-2'>
                          <span className='font-bold text-sm text-[var(--text-color)]'>{item.symbol}</span>
                        </div>
                        <span className='text-[var(--text-color)] opacity-60 text-xs truncate w-48'>{item.name}</span>
                      </div>
                    </div>
                  </li>
                ))}

                {!isLoadingActive && !isSuggestMode && displayList.length === 0 && (
                  <li className='px-4 py-3 text-sm text-[var(--text-color)] opacity-60 text-center'>
                    ไม่พบหุ้น "{symbol}"
                  </li>
                )}
              </ul>
            )}
          </div>

          <div>
            <label className="block text-sm mb-1" style={{ color: 'var(--text-color)' }}>จำนวนหุ้น  <span className='text-[var(--red-text-color)]'>*</span></label>
            <input
              type="number"
              className="w-full px-3 py-2 rounded text-[var(--text-color)] border border-[var(--border-item-light)] focus:outline-none focus:border-[var(--yellow-text-color)]"
              style={{ backgroundColor: 'var(--text-input)' }}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value === '' ? '' : parseFloat(e.target.value))}
              placeholder="เช่น 10"
            />
          </div>

          <div>
            <label className="block text-sm mb-1" style={{ color: 'var(--text-color)' }}>ต้นทุนเฉลี่ย ($)  <span className='text-[var(--red-text-color)]'>*</span></label>
            <input
              type="number"
              className="w-full px-3 py-2 rounded text-[var(--text-color)] border border-[var(--border-item-light)] focus:outline-none focus:border-[var(--yellow-text-color)]"
              style={{ backgroundColor: 'var(--text-input)' }}
              value={avgBuyPrice}
              onChange={(e) => setAvgBuyPrice(e.target.value === '' ? '' : parseFloat(e.target.value))}
              placeholder="เช่น 100.50"
            />
          </div>
        </div>

        {displayMode === 'V1' && (
          <>
            <div className="mb-2">
              <label className="block text-sm my-2"
                style={{ color: 'var(--text-color)' }}
              >สัดส่วนเป้าหมายในพอร์ตนี้ (%)</label>
              <input
                type="number"
                className="w-full px-3 py-2 rounded text-[var(--text-color)] border border-[var(--border-item-light)] focus:outline-none focus:border-[var(--yellow-text-color)]"
                style={{ backgroundColor: 'var(--text-input)' }}
                placeholder="เช่น 50"
                value={ratio || ''}
                onChange={e => setRatio(Number(e.target.value))}
                min={0}
                max={100}
                step={0.01}
              />
            </div>
            <div className=" text-xs mb-4"
              style={{ color: 'var(--yellow-text-color)' }}
            >
              สัดส่วนที่เหลือในพอร์ตนี้: {calculatedRemainRatio}%
              <button
                onClick={() => setIsConfigPopupOpen(true)}
                className="px-3 py-1 rounded mt-4 ml-4"
                style={{
                  backgroundColor: 'var(--red-text-color)', // Use theme variable for button background
                  color: 'var(--text-color)', // Use theme variable for button text color
                }}
              >
                แก้ไขสัดส่วน
              </button>
            </div>
          </>
        )}


        <div className="flex gap-3 justify-end mt-8">
          <button onClick={onClose} className="px-4 py-2 text-[var(--text-color)] opacity-60 hover:opacity-100 transition-opacity">ยกเลิก</button>
          <button
            onClick={handleSave}
            disabled={calculatedRemainRatio < 0}
            className={`px-6 py-2 rounded-lg font-bold transition-transform active:scale-95 ${calculatedRemainRatio < 0
                ? 'bg-[var(--button-bg-color)] text-[var(--text-color)] opacity-50 cursor-not-allowed' // สไตล์ตอนโดนปิดใช้งาน
                : 'bg-[var(--button-edit-bg)] text-[var(--text-black)] hover:brightness-95' // สไตล์ปกติ
              }`}          >
            บันทึก
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddEditStock;